/* @jsx h */
import { defineComponent, h, computed, ref } from "vue";
import { RightSheet } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";

type MarketplaceRestaurant = {
  id: number;
  name: string;
  slug: string | null;
  status: string;
  address: string | null;
  description: string | null;
  currency: string | null;
  timezone: string | null;
};

type CartItem = {
  productId: number;
  name: string;
  price: string | null;
  quantity: number;
};

type Props = {
  restaurant: MarketplaceRestaurant | null;
  isOpen: boolean;
  onClose: () => void;
};

const formatPrice = (
  price: number | string | null,
  currency: string | null,
) => {
  const amount =
    typeof price === "number" ? price : parseFloat(String(price ?? "0"));
  if (isNaN(amount)) return "—";
  const cur = currency ?? "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${cur} ${amount.toFixed(2)}`;
  }
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

const STATUS_CONFIG: Record<
  string,
  { label: string; class: string; icon: string }
> = {
  new: { label: "Received", class: "badge-info text-info", icon: "inbox" },
  preparing: {
    label: "Preparing",
    class: "badge-warning text-warning",
    icon: "restaurant",
  },
  ready: {
    label: "Ready",
    class: "badge-success text-success",
    icon: "check_circle",
  },
  closed: {
    label: "Fulfilled",
    class: "badge-ghost text-base-content/50",
    icon: "task_alt",
  },
};

export const CatalogSheet = defineComponent<Props>({
  name: "CatalogSheet",
  props: ["restaurant", "isOpen", "onClose"],
  setup(props) {
    const trpc = useTrpc();
    const queryClient = useQueryClient();
    const cart = ref<CartItem[]>([]);
    const view = ref<"catalog" | "checkout" | "success">("catalog");
    const notes = ref("");
    const submittedOrderId = ref<number | null>(null);

    const catalogQuery = useQuery({
      queryKey: computed(() => ["marketplace-catalog", props.restaurant?.id]),
      queryFn: () =>
        trpc.marketplace.getRestaurantCatalog.query({
          restaurantId: props.restaurant!.id,
        }),
      enabled: computed(() => props.isOpen && props.restaurant != null),
      staleTime: 30_000,
    });

    const placeOrderMutation = useMutation({
      mutationFn: (input: {
        sellerRestaurantId: number;
        items: { product_id: number; quantity: number }[];
        expectedTotal: number;
        notes?: string;
      }) => trpc.marketplace.placeOrder.mutate(input),
      onSuccess: (data) => {
        submittedOrderId.value = data.order_id;
        view.value = "success";
        queryClient.invalidateQueries({
          queryKey: ["marketplace-purchase-orders"],
        });
      },
      onError: async (error: any) => {
        // The seller changed prices after these items were added: reload the
        // catalog and reprice the cart so the buyer sees the new total before
        // trying again. Items no longer for sale are dropped.
        if (error?.data?.code !== "CONFLICT") return;
        const { data } = await catalogQuery.refetch();
        if (!data) return;
        cart.value = cart.value
          .map((item) => {
            const product = data.products.find((p) => p.id === item.productId);
            return product ? { ...item, price: product.price } : null;
          })
          .filter((item): item is CartItem => item !== null);
      },
    });

    const categoriesWithProducts = computed(() => {
      const data = catalogQuery.data.value;
      if (!data) return [];
      return data.categories
        .filter((c) => c.product_ids.length > 0)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((cat) => ({
          ...cat,
          products: cat.product_ids
            .map((id) => data.products.find((p) => p.id === id))
            .filter(Boolean) as (typeof data.products)[number][],
        }))
        .filter((c) => c.products.length > 0);
    });

    const uncategorizedProducts = computed(() => {
      const data = catalogQuery.data.value;
      if (!data) return [];
      const categorizedIds = new Set(
        data.categories.flatMap((c) => c.product_ids),
      );
      return data.products.filter((p) => !categorizedIds.has(p.id));
    });

    const cartTotal = computed(() =>
      cart.value.reduce((sum, item) => {
        const price = parseFloat(item.price ?? "0") || 0;
        return sum + price * item.quantity;
      }, 0),
    );

    const cartItemCount = computed(() =>
      cart.value.reduce((n, i) => n + i.quantity, 0),
    );

    const getCartQty = (productId: number) =>
      cart.value.find((i) => i.productId === productId)?.quantity ?? 0;

    const addToCart = (product: {
      id: number;
      name: string;
      price: string | null;
    }) => {
      const existing = cart.value.find((i) => i.productId === product.id);
      if (existing) {
        existing.quantity++;
      } else {
        cart.value.push({
          productId: product.id,
          name: product.name,
          price: product.price,
          quantity: 1,
        });
      }
    };

    const removeFromCart = (productId: number) => {
      const idx = cart.value.findIndex((i) => i.productId === productId);
      if (idx === -1) return;
      if (cart.value[idx].quantity > 1) {
        cart.value[idx].quantity--;
      } else {
        cart.value.splice(idx, 1);
      }
    };

    const handleClose = () => {
      cart.value = [];
      notes.value = "";
      view.value = "catalog";
      submittedOrderId.value = null;
      props.onClose();
    };

    const handlePlaceOrder = async () => {
      if (!props.restaurant) return;
      await placeOrderMutation.mutateAsync({
        sellerRestaurantId: props.restaurant.id,
        items: cart.value.map((i) => ({
          product_id: i.productId,
          quantity: i.quantity,
        })),
        expectedTotal: cartTotal.value,
        notes: notes.value || undefined,
      });
    };

    // ─── Product card ──────────────────────────────────────────────────────────

    const renderProductCard = (product: {
      id: number;
      name: string;
      price: string | null;
      special_price: string | null;
      description: string | null;
      color: string | null;
    }) => {
      const qty = getCartQty(product.id);
      const cur = props.restaurant?.currency ?? null;
      // The order is charged at `price` (marketplace_create_order reads it
      // server-side), so that's the only price shown.
      const displayPrice = product.price;

      return (
        <div
          key={product.id}
          class="flex items-start gap-3 p-3 rounded-xl border border-base-content/8 bg-base-100 hover:border-primary/25 hover:bg-base-200/30 transition-all duration-100"
        >
          <div
            class="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
            style={
              product.color
                ? `background: ${product.color}22`
                : "background: oklch(var(--b2))"
            }
          >
            <span class="material-symbols-outlined text-xl text-base-content/30">
              lunch_dining
            </span>
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-base-content leading-snug">
              {product.name}
            </p>
            {product.description && (
              <p class="text-xs text-base-content/45 mt-0.5 line-clamp-2 leading-relaxed">
                {product.description}
              </p>
            )}
            <div class="flex items-center gap-2 mt-1.5">
              {displayPrice ? (
                <span class="text-sm font-bold text-primary">
                  {formatPrice(displayPrice, cur)}
                </span>
              ) : (
                <span class="text-xs text-base-content/40 italic">
                  Price on request
                </span>
              )}
            </div>
          </div>

          <div class="flex items-center gap-1 flex-shrink-0 mt-0.5">
            {qty > 0 ? (
              <div class="flex items-center gap-1">
                <button
                  class="btn btn-circle btn-xs btn-ghost border border-base-content/12"
                  onClick={() => removeFromCart(product.id)}
                >
                  <span
                    class="material-symbols-outlined"
                    style="font-size: 14px"
                  >
                    remove
                  </span>
                </button>
                <span class="w-5 text-center text-sm font-bold tabular-nums text-primary">
                  {qty}
                </span>
                <button
                  class="btn btn-circle btn-xs btn-primary"
                  onClick={() => addToCart(product)}
                >
                  <span
                    class="material-symbols-outlined"
                    style="font-size: 14px"
                  >
                    add
                  </span>
                </button>
              </div>
            ) : (
              <button
                class="btn btn-xs btn-ghost border border-base-content/12 gap-1 hover:btn-primary hover:border-transparent transition-colors"
                onClick={() => addToCart(product)}
              >
                <span class="material-symbols-outlined" style="font-size: 14px">
                  add
                </span>
                Add
              </button>
            )}
          </div>
        </div>
      );
    };

    // ─── Catalog view ──────────────────────────────────────────────────────────

    const renderCatalog = () => {
      if (catalogQuery.isLoading.value) {
        return (
          <div class="flex flex-col items-center justify-center py-24 gap-3">
            <span class="loading loading-spinner loading-md text-primary opacity-50" />
            <p class="text-sm text-base-content/40">Loading catalog…</p>
          </div>
        );
      }

      if (catalogQuery.isError.value) {
        return (
          <div class="flex flex-col items-center justify-center py-24 gap-3">
            <span class="material-symbols-outlined text-5xl text-error/40">
              error
            </span>
            <p class="text-sm text-error">Failed to load catalog</p>
          </div>
        );
      }

      const cats = categoriesWithProducts.value;
      const uncategorized = uncategorizedProducts.value;

      if (cats.length === 0 && uncategorized.length === 0) {
        return (
          <div class="flex flex-col items-center justify-center py-24 gap-3 text-base-content/35">
            <span class="material-symbols-outlined text-5xl opacity-40">
              inventory_2
            </span>
            <p class="text-sm">No products available</p>
          </div>
        );
      }

      return (
        <div class="space-y-6 pb-4">
          {cats.map((cat) => (
            <div key={cat.id}>
              <div class="flex items-center gap-2 mb-2.5">
                {cat.color && (
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    style={`background: ${cat.color}`}
                  />
                )}
                <h3 class="text-[11px] font-bold uppercase tracking-[0.09em] text-base-content/45">
                  {cat.name}
                </h3>
                <span class="text-[10px] text-base-content/25 font-medium">
                  ({cat.products.length})
                </span>
              </div>
              <div class="space-y-2">{cat.products.map(renderProductCard)}</div>
            </div>
          ))}

          {uncategorized.length > 0 && (
            <div>
              <h3 class="text-[11px] font-bold uppercase tracking-[0.09em] text-base-content/45 mb-2.5">
                Other Products
              </h3>
              <div class="space-y-2">
                {uncategorized.map(renderProductCard)}
              </div>
            </div>
          )}
        </div>
      );
    };

    // ─── Checkout view (full-panel, mimics customer CheckoutSheet) ─────────────

    const renderCheckout = () => {
      const r = props.restaurant!;
      const cur = r.currency ?? null;

      return (
        <div class="flex flex-col h-full">
          {/* Back header */}
          <div class="flex items-center gap-3 px-4 py-3 border-b border-base-content/8 flex-shrink-0">
            <button
              class="btn btn-circle btn-sm btn-ghost"
              onClick={() => {
                view.value = "catalog";
              }}
            >
              <span class="material-symbols-outlined text-lg">arrow_back</span>
            </button>
            <span class="font-semibold text-base-content">Review Order</span>
          </div>

          <div class="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Supplier info */}
            <div class="rounded-xl border border-base-content/8 bg-base-200/30 p-4">
              <p class="text-xs text-base-content/45 uppercase tracking-wider font-semibold mb-1">
                Ordering from
              </p>
              <div class="flex items-center gap-2.5 mt-1">
                <div class="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <span class="text-sm font-bold text-primary">
                    {getInitials(r.name)}
                  </span>
                </div>
                <div>
                  <p class="font-semibold text-sm text-base-content">
                    {r.name}
                  </p>
                  {r.address && (
                    <p class="text-xs text-base-content/50 flex items-center gap-1">
                      <span class="material-symbols-outlined text-xs">
                        location_on
                      </span>
                      {r.address}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Order items */}
            <div class="rounded-xl border border-base-content/8 bg-base-100 overflow-hidden">
              <div class="px-4 py-2.5 border-b border-base-content/8 bg-base-200/40">
                <p class="text-xs font-bold uppercase tracking-wider text-base-content/45">
                  Items ({cartItemCount.value})
                </p>
              </div>
              <div class="divide-y divide-base-content/6">
                {cart.value.map((item) => (
                  <div
                    key={item.productId}
                    class="flex items-center justify-between px-4 py-3"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <span class="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                        {item.quantity}
                      </span>
                      <span class="text-sm text-base-content truncate">
                        {item.name}
                      </span>
                    </div>
                    <div class="flex items-center gap-3 flex-shrink-0 ml-3">
                      <span class="text-sm font-semibold text-base-content tabular-nums">
                        {formatPrice(
                          parseFloat(item.price ?? "0") * item.quantity,
                          cur,
                        )}
                      </span>
                      <button
                        class="btn btn-circle btn-xs btn-ghost text-error/50 hover:text-error hover:bg-error/10"
                        onClick={() => {
                          cart.value = cart.value.filter(
                            (i) => i.productId !== item.productId,
                          );
                          if (cart.value.length === 0) view.value = "catalog";
                        }}
                      >
                        <span
                          class="material-symbols-outlined"
                          style="font-size: 14px"
                        >
                          delete
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bill summary */}
            <div class="rounded-xl border border-base-content/8 bg-base-100 overflow-hidden">
              <div class="px-4 py-2.5 border-b border-base-content/8 bg-base-200/40">
                <p class="text-xs font-bold uppercase tracking-wider text-base-content/45">
                  Bill Summary
                </p>
              </div>
              <div class="p-4 space-y-2.5">
                <div class="flex justify-between text-sm">
                  <span class="text-base-content/60">Subtotal</span>
                  <span class="font-medium">
                    {formatPrice(cartTotal.value, cur)}
                  </span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-base-content/60">Delivery fee</span>
                  <span class="text-base-content/40 italic text-xs">
                    To be agreed
                  </span>
                </div>
                <div class="border-t border-base-content/8 pt-2.5 flex justify-between">
                  <span class="font-semibold text-sm">
                    Total (excl. delivery)
                  </span>
                  <span class="font-bold text-primary">
                    {formatPrice(cartTotal.value, cur)}
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery section */}
            <div class="rounded-xl border border-base-content/8 bg-base-100 overflow-hidden">
              <div class="px-4 py-2.5 border-b border-base-content/8 bg-base-200/40">
                <p class="text-xs font-bold uppercase tracking-wider text-base-content/45">
                  Delivery Method
                </p>
              </div>
              <div class="p-4">
                <div class="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5">
                  <span class="material-symbols-outlined text-primary text-xl">
                    local_shipping
                  </span>
                  <div>
                    <p class="text-sm font-semibold text-base-content">
                      Delivery to your restaurant
                    </p>
                    <p class="text-xs text-base-content/50 mt-0.5">
                      Delivery address will be confirmed with the supplier
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div class="rounded-xl border border-base-content/8 bg-base-100 overflow-hidden">
              <div class="px-4 py-2.5 border-b border-base-content/8 bg-base-200/40">
                <p class="text-xs font-bold uppercase tracking-wider text-base-content/45">
                  Notes{" "}
                  <span class="normal-case font-normal text-base-content/30">
                    (optional)
                  </span>
                </p>
              </div>
              <div class="p-4">
                <textarea
                  class="textarea textarea-bordered w-full text-sm resize-none bg-base-100"
                  rows={3}
                  placeholder="Delivery instructions, special requirements…"
                  value={notes.value}
                  onInput={(e: Event) => {
                    notes.value = (e.target as HTMLTextAreaElement).value;
                  }}
                />
              </div>
            </div>
          </div>

          {/* Submit footer */}
          <div class="border-t border-base-content/8 bg-base-100 p-4 flex-shrink-0">
            {placeOrderMutation.isError.value && (
              <div class="alert alert-error alert-sm mb-3 py-2 text-sm">
                <span class="material-symbols-outlined text-base">error</span>
                {(placeOrderMutation.error.value as any)?.data?.code ===
                "CONFLICT"
                  ? "The seller's prices have changed. Please review your cart."
                  : "Failed to place order. Please try again."}
              </div>
            )}
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-base-content/60">
                {cartItemCount.value}{" "}
                {cartItemCount.value === 1 ? "item" : "items"}
              </span>
              <span class="text-lg font-bold text-primary">
                {formatPrice(cartTotal.value, cur)}
              </span>
            </div>
            <button
              class="btn btn-primary btn-block gap-2 shadow-sm"
              disabled={
                placeOrderMutation.isPending.value || cart.value.length === 0
              }
              onClick={handlePlaceOrder}
            >
              {placeOrderMutation.isPending.value ? (
                <>
                  <span class="loading loading-spinner loading-sm" />
                  Placing order…
                </>
              ) : (
                <>
                  <span class="material-symbols-outlined text-lg">send</span>
                  Place Purchase Order
                </>
              )}
            </button>
          </div>
        </div>
      );
    };

    // ─── Success view ──────────────────────────────────────────────────────────

    const renderSuccess = () => (
      <div class="flex flex-col items-center justify-center h-full p-8 text-center gap-5">
        <div class="w-20 h-20 rounded-full bg-success/15 flex items-center justify-center">
          <span class="material-symbols-outlined text-4xl text-success">
            check_circle
          </span>
        </div>
        <div>
          <h3 class="text-xl font-bold text-base-content">Order Placed!</h3>
          <p class="text-sm text-base-content/55 mt-1.5 max-w-xs leading-relaxed">
            Your purchase order has been sent to{" "}
            <strong>{props.restaurant?.name}</strong>. They will prepare it and
            arrange delivery.
          </p>
          {submittedOrderId.value && (
            <p class="text-xs text-base-content/35 mt-2 font-mono">
              Order #{submittedOrderId.value}
            </p>
          )}
        </div>
        <div class="flex flex-col gap-2 w-full max-w-xs">
          <button
            class="btn btn-primary btn-sm gap-1.5"
            onClick={() => {
              cart.value = [];
              notes.value = "";
              view.value = "catalog";
              submittedOrderId.value = null;
            }}
          >
            <span class="material-symbols-outlined text-base">storefront</span>
            Continue shopping
          </button>
          <button class="btn btn-ghost btn-sm" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    );

    // ─── Cart bar (shown in catalog view) ────────────────────────────────────

    const renderCartBar = () => {
      if (cart.value.length === 0 || view.value !== "catalog") return null;
      const cur = props.restaurant?.currency ?? null;

      return (
        <div class="border-t border-base-content/8 bg-base-100 px-4 py-3 flex-shrink-0">
          <button
            class="btn btn-primary btn-block gap-3 shadow-sm"
            onClick={() => {
              view.value = "checkout";
            }}
          >
            <div class="flex items-center gap-2 flex-1">
              <span class="badge badge-sm bg-white/20 text-accent font-bold border-0">
                {cartItemCount.value}
              </span>
              <span>Review Order</span>
            </div>
            <span class="font-bold">{formatPrice(cartTotal.value, cur)}</span>
          </button>
        </div>
      );
    };

    // ─── Sheet title ──────────────────────────────────────────────────────────

    return () => {
      if (!props.restaurant) return null;

      const r = props.restaurant;
      const initials = getInitials(r.name);

      const sheetTitle = (
        <div class="flex items-center gap-3 min-w-0">
          <div class="w-9 h-9 rounded-xl bg-primary/12 flex items-center justify-center flex-shrink-0">
            <span class="text-sm font-bold text-primary">{initials}</span>
          </div>
          <div class="min-w-0">
            <p class="font-bold text-base-content leading-tight truncate text-base">
              {r.name}
            </p>
            <div class="flex items-center gap-2 mt-0.5">
              {r.address && (
                <p class="text-xs text-base-content/45 flex items-center gap-0.5 truncate">
                  <span
                    class="material-symbols-outlined"
                    style="font-size: 12px"
                  >
                    location_on
                  </span>
                  {r.address}
                </p>
              )}
              {r.currency && (
                <span class="badge badge-xs badge-ghost font-mono flex-shrink-0">
                  {r.currency}
                </span>
              )}
            </div>
          </div>
        </div>
      );

      return (
        <RightSheet
          isOpen={props.isOpen}
          title={view.value === "catalog" ? sheetTitle : undefined}
          onClose={handleClose}
          width="540px"
        >
          <div class="flex flex-col h-full overflow-hidden">
            {view.value === "catalog" && (
              <>
                <div class="flex-1 overflow-y-auto p-4">{renderCatalog()}</div>
                {renderCartBar()}
              </>
            )}
            {view.value === "checkout" && renderCheckout()}
            {view.value === "success" && renderSuccess()}
          </div>
        </RightSheet>
      );
    };
  },
});
